import React from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion'; // Import framer-motion

function Testimonials() {
  const { t } = useTranslation();

  const testimonials = [
    {
      quote: t('testimonials.testimonial1.quote'),
      author: t('testimonials.testimonial1.author'),
      location: t('testimonials.testimonial1.location'),
      image: '/assets/images/testimonials/person1.jpg',
    },
    {
      quote: t('testimonials.testimonial2.quote'),
      author: t('testimonials.testimonial2.author'),
      location: t('testimonials.testimonial2.location'),
      image: '/assets/images/testimonials/person2.jpg',
    },
    {
      quote: t('testimonials.testimonial3.quote'),
      author: t('testimonials.testimonial3.author'),
      location: t('testimonials.testimonial3.location'),
      image: '/assets/images/testimonials/person3.jpg',
    },
    {
      quote: t('testimonials.testimonial4.quote'),
      author: t('testimonials.testimonial4.author'),
      location: t('testimonials.testimonial4.location'),
      image: '/assets/images/testimonials/person4.jpg',
    },
  ];

  // Define the animation variants for the Testimonials Section
  const testimonialsVariants = {
    hidden: { opacity: 0, y: 50 }, // Start 50px below and invisible
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { 
        duration: 0.8, 
        ease: 'easeOut',
        staggerChildren: 0.2 // Stagger the animation of child elements (testimonial cards)
      } 
    },
  };

  // Animation variants for individual testimonial cards
  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
  };

  return (
    <div>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{t('testimonials.seoTitle')}</title>
        <meta name="description" content={t('testimonials.seoDescription')} />
      </Helmet>

      {/* Hero Section */}
      <section
        className="relative bg-cover bg-center h-[400px] flex items-center justify-center text-white"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('/assets/images/testimonial.jpeg')`,
        }}
      >
        <div className="text-center px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {t('testimonials.heroTitle')}
          </h1>
          <p className="text-lg md:text-xl">
            {t('testimonials.heroSubtitle')}
          </p>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="bg-secondary py-16">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible" // Animate when the element comes into view
            viewport={{ once: true, amount: 0.2 }} // Trigger animation once when 20% of the element is visible
            variants={testimonialsVariants}
          >
            <h2 className="text-3xl md:text-4xl font-semibold text-textPrimary text-center mb-6">
              {t('testimonials.sectionTitle')}
            </h2>
            <p className="text-lg text-textPrimary text-center mb-12">
              {t('testimonials.sectionSubtitle')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  variants={cardVariants}
                  className="bg-white shadow-md rounded-lg p-6 transform hover:scale-105 transition-transform duration-300"
                >
                  <div className="flex items-center mb-4">
                    <img
                      src={testimonial.image}
                      alt={testimonial.author}
                      className="w-12 h-12 rounded-full mr-4 object-cover"
                      onError={(e) => (e.target.src = 'https://via.placeholder.com/150?text=Person')}
                    />
                    <div>
                      <p className="text-textPrimary font-semibold">{testimonial.author}</p>
                      <p className="text-accent text-sm">{testimonial.location}</p>
                    </div>
                  </div>
                  <p className="text-textPrimary italic">"{testimonial.quote}"</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="bg-primary text-white py-12">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-semibold mb-6">
            {t('testimonials.ctaTitle')}
          </h2>
          <a
            href="/contact"
            className="bg-accent text-white px-6 py-3 rounded-lg text-lg font-semibold hover:bg-yellow-600 transition-colors duration-200"
          >
            {t('testimonials.ctaButton')}
          </a>
        </div>
      </section>
    </div>
  );
}

export default Testimonials;